// Lead Emails tab — all messages attached to this lead (in/out across every
// connected mailbox). Auto-attachment happens at ingest time by matching the
// lead's email address against message participants; sending from this panel
// also force-attaches via the leadId on /send.
//
// Pre-flight: needs at least one active mailbox. If none, show a connect CTA.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, AlertCircle, Mail, MailOpen, Reply, ReplyAll, Forward,
  ArrowDownLeft, ArrowUpRight, CheckCircle2, RefreshCw, X, Paperclip,
} from "lucide-react";
import toast from "react-hot-toast";
import { Button, Badge } from "../../atoms";
import { useLeadEmails, useEmailAccounts, useSyncLeadEmails, useEmailThread } from "../../hooks/useEmails";
import useFocusRow, { FOCUS_ROW_STYLE } from "../../hooks/useFocusRow";
import EmailComposeModal from "../../overlays/EmailComposeModal";
import Modal from "../../overlays/Modal";
import { getOutboundHeaderSubtitle, getOutboundHeaderTitle, getRecipientLines } from "../../utils/emailDisplayUtils";
import { replyAllRecipients } from "../../utils/emailComposeUtils";

function fmtDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  if (sameDay) return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const FILTER_TABS = [
  { id: "unread", label: "Unread" },
  { id: "read",   label: "Read"   },
  { id: "sent",   label: "Sent"   },
];

function messageKey(m) {
  return `${m.accountId}:${m.providerMessageId}`;
}

function matchesTab(msg, tab) {
  const inbound = msg.direction !== "outbound";
  if (tab === "sent")   return msg.direction === "outbound";
  if (tab === "unread") return inbound && !msg.isRead;
  if (tab === "read")   return inbound && msg.isRead;
  return true;
}

function tabCounts(messages) {
  let unread = 0, read = 0, sent = 0;
  for (const m of messages) {
    if (m.direction === "outbound") sent += 1;
    else if (m.isRead) read += 1;
    else unread += 1;
  }
  return { unread, read, sent };
}

// ─── Shadow-DOM HTML email renderer (same as Inbox) ──────────────────────────

function sanitizeEmailHtml(html) {
  if (!html) return "";
  let out = String(html);
  out = out.replace(/<script[\s\S]*?<\/script\s*>/gi, "");
  out = out.replace(/<(iframe|object|embed|link|meta)\b[^>]*>/gi, "");
  out = out.replace(/\s+on[a-z]+\s*=\s*"[^"]*"/gi, "");
  out = out.replace(/\s+on[a-z]+\s*=\s*'[^']*'/gi, "");
  out = out.replace(/\s+on[a-z]+\s*=\s*[^\s>]+/gi, "");
  out = out.replace(/(href|src|action)\s*=\s*"\s*javascript:[^"]*"/gi, '$1="#"');
  out = out.replace(/(href|src|action)\s*=\s*'\s*javascript:[^']*'/gi, "$1='#'");
  return out;
}

function HtmlMessageBody({ html, collapsed }) {
  const hostRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);
  const COLLAPSED_MAX = 480;
  const isCollapsed = collapsed && contentHeight > COLLAPSED_MAX;
  const sanitized = useMemo(() => sanitizeEmailHtml(html), [html]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let root = host.shadowRoot;
    if (!root) root = host.attachShadow({ mode: "open" });
    root.innerHTML = `
      <style>
        :host { display: block; width: 100%; }
        .crm-mail-root {
          all: initial;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
          color: inherit; font-size: 14px; line-height: 1.6;
          display: block; width: 100%; word-wrap: break-word; overflow-wrap: anywhere;
        }
        .crm-mail-root img, .crm-mail-root video { max-width: 100% !important; height: auto !important; display: inline-block; }
        .crm-mail-root table { max-width: 100% !important; border-collapse: collapse; }
        .crm-mail-root a { color: inherit; text-decoration: underline; }
        .crm-mail-root blockquote { margin: 12px 0; padding: 4px 12px; border-left: 3px solid rgba(127,127,127,0.25); color: rgba(127,127,127,0.9); }
        .crm-mail-root pre { font-family: ui-monospace, monospace; background: rgba(127,127,127,0.08); padding: 10px; border-radius: 6px; overflow-x: auto; }
      </style>
      <div class="crm-mail-root">${sanitized}</div>
    `;
    const onClick = (e) => {
      const t = e.target.closest && e.target.closest("a");
      if (t && t.getAttribute("href")) {
        e.preventDefault();
        const href = t.getAttribute("href");
        if (href && href !== "#") window.open(href, "_blank", "noopener,noreferrer");
      }
    };
    root.addEventListener("click", onClick);
    const rootEl = root.querySelector(".crm-mail-root");
    let cancelled = false;
    const timeouts = [];
    function measure() {
      if (cancelled || !rootEl) return;
      const h = rootEl.scrollHeight || rootEl.offsetHeight || 0;
      if (h) setContentHeight((prev) => (Math.abs(prev - h) > 2 ? h : prev));
    }
    measure();
    [50, 200, 500, 1000, 2000].forEach((d) => timeouts.push(setTimeout(measure, d)));
    let ro;
    try { ro = new ResizeObserver(measure); if (rootEl) ro.observe(rootEl); } catch (_) {}
    if (rootEl) {
      rootEl.querySelectorAll("img").forEach((img) => {
        if (!img.complete) {
          img.addEventListener("load", measure, { once: true });
          img.addEventListener("error", measure, { once: true });
        }
      });
    }
    return () => { cancelled = true; timeouts.forEach((t) => clearTimeout(t)); ro?.disconnect(); root.removeEventListener("click", onClick); };
  }, [sanitized]);

  // Lead-drawer email detail already has Expand/Collapse in the header — no
  // inline "Click to expand" overlay here (Inbox keeps its own).
  return (
    <div style={{ maxHeight: isCollapsed ? COLLAPSED_MAX : "none", overflow: isCollapsed ? "hidden" : "visible", transition: "max-height 200ms ease" }}>
      <div ref={hostRef} />
    </div>
  );
}

function MessageBodyView({ msg, collapsed }) {
  const [forceText, setForceText] = useState(false);
  const hasHtml = !!(msg.bodyHtml && msg.bodyHtml.trim());
  const hasText = !!(msg.bodyText && msg.bodyText.trim());
  const linkBtn = { padding: 0, background: "transparent", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 500, color: "var(--color-text-secondary)", textDecoration: "underline", textUnderlineOffset: 2 };

  if (hasHtml && !forceText) {
    return (
      <div>
        <HtmlMessageBody html={msg.bodyHtml} collapsed={collapsed} />
        {hasText && <button type="button" onClick={() => setForceText(true)} style={{ ...linkBtn, marginTop: 10, display: "block" }}>View plain text</button>}
      </div>
    );
  }
  return (
    <div>
      <div style={{
        fontSize: 13.5, color: "var(--color-text-primary)", whiteSpace: "pre-wrap",
        wordBreak: "break-word", lineHeight: 1.6,
        maxHeight: collapsed ? 320 : "none", overflow: collapsed ? "hidden" : "visible",
      }}>
        {msg.bodyText || msg.snippet || ""}
      </div>
      {hasHtml && forceText && (
        <button type="button" onClick={() => setForceText(false)} style={{ ...linkBtn, marginTop: 10 }}>View rich template</button>
      )}
    </div>
  );
}

// ─── Action button (same style as Inbox) ─────────────────────────────────────

function ActionBtn({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 12px", fontSize: 12, fontWeight: 600, background: "transparent", border: "1px solid var(--color-border-subtle)", borderRadius: 8, cursor: "pointer", color: "var(--color-text-secondary)", transition: "all 120ms" }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--crm-primary)"; e.currentTarget.style.color = "var(--crm-primary)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--color-border-subtle)"; e.currentTarget.style.color = "var(--color-text-secondary)"; }}
    >
      <Icon size={12} /> {label}
    </button>
  );
}

// ─── Email detail modal ───────────────────────────────────────────────────────

function EmailDetailModal({ open, onClose, msg, onReply, onReplyAll }) {
  const [collapsed, setCollapsed] = useState(true);

  // Reset collapse whenever a new message is opened
  useEffect(() => { if (open) setCollapsed(true); }, [open, msg?.providerMessageId]);

  const { data: threadData, isLoading } = useEmailThread(
    open && msg ? msg.accountId : null,
    open && msg ? msg.providerThreadId : null,
  );

  const fullMsg = useMemo(() => {
    if (!threadData?.messages) return null;
    return threadData.messages.find((m) => m.providerMessageId === msg?.providerMessageId)
      || threadData.messages[threadData.messages.length - 1];
  }, [threadData, msg?.providerMessageId]);

  const outbound  = msg?.direction === "outbound";
  const tone      = outbound ? "var(--crm-primary)" : "var(--crm-success)";
  const IconArrow = outbound ? ArrowUpRight : ArrowDownLeft;
  const toLine    = msg?.toAddresses?.join(", ") || "";
  const ccLine    = msg?.ccAddresses?.length ? msg.ccAddresses.join(", ") : null;

  const toggleBtn = {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "4px 10px", fontSize: 11, fontWeight: 600,
    background: "var(--color-bg-muted)", border: "1px solid var(--color-border-subtle)",
    borderRadius: 6, cursor: "pointer", color: "var(--color-text-secondary)",
  };

  return (
    <Modal open={open} onClose={onClose} width={720}>

      {/* ── Fixed header ── */}
      <div style={{ flexShrink: 0, borderBottom: "1px solid var(--color-border-subtle)" }}>

        {/* Row 1: subject + collapse toggle + close — all always visible */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "16px 20px 10px" }}>
          <h2 style={{ margin: 0, flex: 1, fontSize: 15, fontWeight: 600, lineHeight: 1.35, color: "var(--color-text-primary)", letterSpacing: "-0.01em" }}>
            {msg?.subject || "(no subject)"}
          </h2>
          {/* Collapse / Expand toggle — always in header so no scrolling needed */}
          <button type="button" onClick={() => setCollapsed((v) => !v)} style={toggleBtn}>
            {collapsed ? <><ArrowDownLeft size={11} style={{ transform: "rotate(180deg)" }} /> Expand</> : <><ArrowUpRight size={11} style={{ transform: "rotate(180deg)" }} /> Collapse</>}
          </button>
          <button
            type="button" onClick={onClose}
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 6, border: "1px solid var(--color-border-subtle)", background: "transparent", cursor: "pointer", color: "var(--color-text-secondary)", flexShrink: 0 }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Row 2: sender avatar + name + date + to/cc */}
        <div style={{ padding: "0 20px 14px", display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: 999, background: "var(--color-bg-muted)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <IconArrow size={13} style={{ color: tone }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>
                {outbound ? getOutboundHeaderTitle(msg) : (msg?.fromName ? `${msg.fromName} <${msg.fromAddress}>` : msg?.fromAddress)}
              </div>
              <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 1 }}>
                {msg?.sentAt && new Date(msg.sentAt).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
              </div>
            </div>
          </div>
          {toLine && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", paddingLeft: 38 }}><span style={{ fontWeight: 600 }}>To:</span> {toLine}</div>}
          {ccLine && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", paddingLeft: 38 }}><span style={{ fontWeight: 600 }}>Cc:</span> {ccLine}</div>}
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
        {isLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--color-text-secondary)", fontSize: 13, padding: "40px 0", justifyContent: "center" }}>
            <Loader2 size={14} className="animate-spin" /> Loading message…
          </div>
        )}
        {!isLoading && fullMsg && <MessageBodyView msg={fullMsg} collapsed={collapsed} />}
        {!isLoading && !fullMsg && (
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", padding: "40px 0", textAlign: "center" }}>
            Could not load message body.
          </div>
        )}
        {/* Attachments */}
        {!isLoading && fullMsg?.attachments?.length > 0 && (
          <div style={{ marginTop: 20, paddingTop: 14, borderTop: "1px solid var(--color-border-subtle)" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
              <Paperclip size={11} /> {fullMsg.attachments.length} attachment{fullMsg.attachments.length === 1 ? "" : "s"}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {fullMsg.attachments.map((att, i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 6, border: "1px solid var(--color-border-subtle)", background: "var(--color-bg-muted)", fontSize: 12, color: "var(--color-text-secondary)" }}>
                  <Paperclip size={11} /> {att.filename || "attachment"}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Pinned footer: Reply + Reply All always visible ── */}
      <div style={{ flexShrink: 0, borderTop: "1px solid var(--color-border-subtle)", padding: "12px 20px", display: "flex", gap: 8, background: "var(--color-bg-base)" }}>
        <ActionBtn icon={Reply}    label="Reply"     onClick={() => { onClose(); onReply(msg); }} />
        <ActionBtn icon={ReplyAll} label="Reply All" onClick={() => { onClose(); onReplyAll(msg); }} />
      </div>

    </Modal>
  );
}

// ─── Message row (list item) ──────────────────────────────────────────────────

function MessageRow({ msg, onClick, rowRef }) {
  const outbound = msg.direction === "outbound";
  const IconArrow = outbound ? ArrowUpRight : ArrowDownLeft;
  const tone = outbound ? "var(--crm-primary)" : "var(--crm-success)";
  const unread = !outbound && !msg.isRead;

  return (
    <div
      ref={rowRef}
      onClick={() => onClick(msg)}
      style={{ padding: "12px 14px", borderTop: "1px solid var(--color-border-subtle)", display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--color-bg-muted)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
    >
      <div style={{ width: 28, height: 28, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "var(--color-bg-muted)", color: tone, flexShrink: 0 }}>
        <IconArrow size={14} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
          <div style={{ fontSize: 13, fontWeight: unread ? 700 : 600, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
            {unread && <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: "var(--crm-primary)", flexShrink: 0 }} />}
            {outbound ? getOutboundHeaderTitle(msg) : (msg.fromName || msg.fromAddress)}
          </div>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", flexShrink: 0 }}>{fmtDate(msg.sentAt)}</div>
        </div>
        {outbound && getOutboundHeaderSubtitle(msg) && (
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2, lineHeight: 1.4 }}>{getOutboundHeaderSubtitle(msg)}</div>
        )}
        <div style={{ fontSize: 13, color: "var(--color-text-primary)", margin: "2px 0 4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {msg.subject || "(no subject)"}
        </div>
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.5, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden" }}>
          {msg.snippet || msg.bodyText?.slice(0, 200) || ""}
        </div>
      </div>
    </div>
  );
}

// ─── Main panel ──────────────────────────────────────────────────────────────

export default function EmailsPanel({ leadId, lead, focusItemId, onFocusResolved }) {
  const { data: messages = [], isLoading, isError, error } = useLeadEmails(leadId);
  const { data: accounts = [] } = useEmailAccounts();
  const activeAccounts = useMemo(() => accounts.filter((a) => a.status === "active"), [accounts]);
  const syncMut = useSyncLeadEmails();
  const { register } = useFocusRow(focusItemId, { onResolved: onFocusResolved });

  const [composeOpen, setComposeOpen] = useState(false);
  const [replyTo, setReplyTo]         = useState(null);
  const [composeMode, setComposeMode] = useState("new");
  const [filterTab, setFilterTab]     = useState("unread");
  const [detailMsg, setDetailMsg]     = useState(null);

  const counts   = useMemo(() => tabCounts(messages), [messages]);
  const filtered = useMemo(() => messages.filter((m) => matchesTab(m, filterTab)), [messages, filterTab]);
  const activeTab = FILTER_TABS.find((t) => t.id === filterTab) || FILTER_TABS[0];

  const onSync = async () => {
    if (!lead?.email) { toast.error("Add an email to this lead before syncing."); return; }
    try {
      const r = await syncMut.mutateAsync(leadId);
      if (r?.reason === "no_active_gmail_accounts") { toast("No connected mailbox to sync from.", { icon: "ℹ️" }); return; }
      if (r?.reason === "lead_has_no_email") { toast.error("Lead has no email address."); return; }
      const n = r?.ingested || 0;
      toast.success(n > 0 ? `Synced ${n} new email${n === 1 ? "" : "s"} from Gmail` : "No new emails. Already up to date.");
      // Don't let a capped scan read as "we got everything".
      if (r?.truncated) toast("Hit the per-sync message limit — older mail wasn't scanned.", { icon: "⚠️" });
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || "Sync failed");
    }
  };

  const buildReplyTo = (msg) => ({
    accountId: msg.accountId,
    subject: msg.subject,
    fromAddress: msg.fromAddress,
    toAddresses: msg.toAddresses,
    ccAddresses: msg.ccAddresses,
    replyTo: {
      providerMessageId: msg.providerMessageId,
      providerThreadId: msg.providerThreadId,
      inReplyTo: msg.rfcMessageId || msg.providerMessageId,
      references: [...(msg.references || []), msg.rfcMessageId].filter(Boolean),
      subject: msg.subject,
    },
  });

  const onCompose  = () => { setReplyTo(null); setComposeMode("new"); setComposeOpen(true); };
  const onReply    = (msg) => { setReplyTo(buildReplyTo(msg)); setComposeMode("reply");    setComposeOpen(true); };
  const onReplyAll = (msg) => { setReplyTo(buildReplyTo(msg)); setComposeMode("replyAll"); setComposeOpen(true); };

  if (activeAccounts.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "var(--color-text-secondary)" }}>
        <Mail size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)", marginBottom: 4 }}>Connect a mailbox to see emails</div>
        <div style={{ fontSize: 12, marginBottom: 12 }}>Once you connect Gmail, every message to or from <strong>{lead?.email || "this lead"}</strong> will appear here automatically.</div>
        <div style={{ fontSize: 11 }}>Go to <strong>Settings → Email</strong> to connect.</div>
      </div>
    );
  }

  return (
    <div>
      <style>{FOCUS_ROW_STYLE}</style>

      {/* Connected mailbox banner */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "var(--crm-primary-soft)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, marginBottom: 12 }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: "var(--color-bg-surface)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--crm-primary)", flexShrink: 0 }}>
          <Mail size={14} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 2 }}>
            Connected mailbox{activeAccounts.length > 1 ? "es" : ""}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>
            {activeAccounts.map((a, i) => (
              <React.Fragment key={a._id}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{a.email}</span>
                {i < activeAccounts.length - 1 && <span style={{ color: "var(--color-text-secondary)", fontWeight: 400 }}>·</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
        <Badge tone="success" dot><CheckCircle2 size={11} style={{ marginRight: 4, verticalAlign: -1 }} />Active</Badge>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
          {filtered.length} {filtered.length === 1 ? "message" : "messages"}
          {messages.length !== filtered.length && <span style={{ opacity: 0.75 }}> · {messages.length} total</span>}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" onClick={onSync} disabled={!lead?.email || syncMut.isPending} title={lead?.email ? "Pull matching emails from Gmail" : "Add an email to enable sync"}>
            {syncMut.isPending ? <Loader2 size={14} style={{ marginRight: 6 }} className="animate-spin" /> : <RefreshCw size={14} style={{ marginRight: 6 }} />}
            Sync
          </Button>
          <Button onClick={onCompose} disabled={!lead?.email}>
            <Mail size={14} style={{ marginRight: 6 }} /> New email
          </Button>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {FILTER_TABS.map((tab) => {
          const active = filterTab === tab.id;
          const count = counts[tab.id] ?? 0;
          return (
            <button key={tab.id} type="button" onClick={() => setFilterTab(tab.id)} style={{ height: 30, padding: "0 12px", borderRadius: 999, border: `1px solid ${active ? "var(--crm-primary)" : "var(--color-border-subtle)"}`, background: active ? "var(--crm-primary)" : "var(--color-bg-surface)", color: active ? "#fff" : "var(--color-text-secondary)", fontSize: 12, fontWeight: active ? 600 : 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
              {tab.label}
              <span style={{ fontSize: 11, fontWeight: 600, opacity: active ? 0.9 : 0.7, fontVariantNumeric: "tabular-nums" }}>{count}</span>
            </button>
          );
        })}
      </div>

      <p style={{ margin: "0 0 12px", fontSize: 11, color: "var(--color-text-secondary)", lineHeight: 1.45 }}>
        Live from Gmail — click any email to read it. Read/unread reflects your mailbox now.
      </p>

      {isLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--color-text-secondary)", fontSize: 13, padding: 20, justifyContent: "center" }}>
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      )}
      {isError && (
        <div style={{ background: "var(--crm-danger-soft)", color: "var(--crm-danger)", padding: 12, borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 10 }}>
          <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13 }}>{error?.response?.data?.error || error?.message || "Failed to load"}</div>
        </div>
      )}
      {!isLoading && !isError && messages.length === 0 && (
        <div style={{ padding: 28, textAlign: "center", color: "var(--color-text-secondary)" }}>
          <MailOpen size={28} style={{ opacity: 0.4, marginBottom: 10 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)" }}>No emails yet for this lead</div>
          <div style={{ fontSize: 12, marginTop: 6, lineHeight: 1.55, maxWidth: 360, margin: "6px auto 0" }}>
            {lead?.email ? <>Any message to or from <strong>{lead.email}</strong> will appear here automatically.</> : <>Add an email to this lead so messages can auto-attach here.</>}
          </div>
        </div>
      )}
      {!isLoading && !isError && messages.length > 0 && filtered.length === 0 && (
        <div style={{ padding: 28, textAlign: "center", color: "var(--color-text-secondary)" }}>
          <MailOpen size={28} style={{ opacity: 0.4, marginBottom: 10 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)" }}>No {activeTab.label.toLowerCase()} emails</div>
          <div style={{ fontSize: 12, marginTop: 6 }}>Try another tab — filters use live Gmail read state and send direction.</div>
        </div>
      )}
      {!isLoading && !isError && filtered.length > 0 && (
        <div style={{ border: "1px solid var(--color-border-subtle)", borderRadius: 8, background: "var(--color-bg-surface)", overflow: "hidden" }}>
          {filtered.map((m) => (
            <MessageRow key={messageKey(m)} msg={m} onClick={setDetailMsg} rowRef={register(messageKey(m))} />
          ))}
        </div>
      )}

      {/* Email detail popup */}
      <EmailDetailModal
        open={!!detailMsg}
        onClose={() => setDetailMsg(null)}
        msg={detailMsg}
        onReply={onReply}
        onReplyAll={onReplyAll}
      />

      <EmailComposeModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        mode={composeMode}
        defaultTo={lead?.email || ""}
        defaultSubject=""
        leadId={leadId}
        replyTo={replyTo}
      />
    </div>
  );
}
